// ABOUTME: Customer environment picker view for SHA admin users.
// ABOUTME: Allows selecting and opening customer environments directly.

import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../../theme";
import Text from "../../../../components/theme/text/Text";
import Subheading from "../../../../components/theme/text/Subheading";
import ShLabel from "../../../../components/theme/label/ShLabel";
import { useGetCustomersQuery, useAddTempCustomerMutation } from "../../../../newapi/user/userSlice";
import { hasPermissionForCustomer, updateAuthData } from "../../../../newapi/user/authentication/authSlice";
import { setSelectedEnvironment, setSelectedMenuItem } from "../../../../newapi/global/settingsSlice";
import { updateActive } from "../../../../newapi/warehouse/warehouseSlice";
import { clearAllViewInstances } from "../../../../newapi/global/filter/gridFilterSlice";
import { ENV_TYPES } from "../../../../components/global/Sidebar";
import { UserPrivileges } from "../../../../utils/permissions";
import { getCustomerTypeData } from "../../../../utils/labelColorText";

const Customers = ({ settings = [], ...props }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();

    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const pendingCustomerRef = useRef(null);

    const { data: customersData, isLoading: isLoadingCustomers } = useGetCustomersQuery();
    const [addTempCustomer, { data, isLoading }] = useAddTempCustomerMutation();
    const refreshToken = useSelector((state) => state.auth.refreshToken);
    const permissions = useSelector(state => state.auth.permissions);
    const warehouseId = useSelector(state => state.warehouse.activeId);
    const customerWarehouses = useSelector(state => state.auth.customerWarehouses);
    const authCustomers = useSelector(state => state.auth.customers);

    useEffect(() => {
        props.replaceView({
            ...props.config,
        });

        return () => {
        }
    }, []);

    useEffect(() => {
        if (!isLoading && data) {
            const addedCustomer = data.data?.addedCustomer;
            if (addedCustomer) {
                const isFulfilment = addedCustomer.isFulfilment ?? false;
                const foundWarehouseId = customerWarehouses?.find(x => x.customerId === addedCustomer.id)?.warehouseId ?? null;
                const existingIndex = authCustomers?.findIndex(c => c.id === addedCustomer.id) ?? -1;
                const customerIndex = existingIndex !== -1 ? existingIndex + 1 : (authCustomers?.length ?? 0) + 1;

                dispatch(setSelectedEnvironment({
                    selectedId: addedCustomer.id,
                    selectedEnvironmentType: ENV_TYPES[100],
                    selectedEnvironmentIndex: customerIndex,
                    selectedEnvironmentName: addedCustomer.name,
                    selectedEnvironmentActive: 'core',
                    isFulfilment: isFulfilment,
                    relationCustomerId: 0,
                    relationRootId: 0
                }));

                if (foundWarehouseId) {
                    dispatch(updateActive({ id: foundWarehouseId, name: '' }));
                } else {
                    dispatch(updateActive({ id: -1, name: t("all_warehouses") }));
                }

                const canAccessDashboard = hasPermissionForCustomer({
                    permissions,
                    customerId: addedCustomer.id,
                    warehouseId,
                    requiredPermissions: [UserPrivileges.DASHBOARD_READ]
                });

                if (canAccessDashboard) {
                    navigate('/warehouse/dashboard');
                    dispatch(setSelectedMenuItem("warehouse-dashboard"));
                } else {
                    navigate('/warehouse/outbound/orders');
                    dispatch(setSelectedMenuItem("warehouse-outbound-orders"));
                }

                dispatch(clearAllViewInstances());
            }

            dispatch(updateAuthData(data.data));
        }
    }, [data, isLoading]);

    const handleOpen = (customer) => {
        pendingCustomerRef.current = { id: customer.id };
        addTempCustomer({
            customerId: customer.id,
            password: "",
            refresh_token: refreshToken,
        });
    };

    const customers = customersData?.data?.customers ?? customersData?.customers ?? [];
    const filteredCustomers = customers.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const DB_TYPE_ORDER = ['production', 'testing', 'development', 'inactive', 'unknown'];

    const groupedCustomers = DB_TYPE_ORDER.reduce((acc, type) => {
        const matched = filteredCustomers.filter(c => (c.db_type ?? 'unknown') === type);
        if (matched.length > 0) {
            acc.push({ type, customers: matched });
        }
        return acc;
    }, []);

    return (
        <Box
            sx={{
                p: 4,
                pt:3,
                "@media screen and (max-width: 47.9375em)": {
                    px:3,
                    pt:"48px !important",
                    pb:"24px !important",
                }
            }}
        >
            <Box
                sx={{
                    display:'flex',
                    justifyContent:'space-between',
                    alignItems:"center",

                    "@media screen and (max-width: 47.9375em)": {
                        flexDirection:'column',
                        alignItems:"flex-start",
                        gap:1,
                    }
                }}
            >
                <Subheading>{t("open_customer")}</Subheading>

                {/* Search */}
                <InputBase
                    sx={{
                        minWidth:300,
                        color: colors.txt["primary"],
                        fontSize: 14,
                        height: 40,
                        px: 2,
                        borderRadius: 5,
                        backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],

                        "@media screen and (max-width: 47.9375em)": {
                            height:32,
                            width:1,
                        }
                    }}
                    placeholder={t("search")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </Box>

            {/* Customer list grouped by db_type */}
            <Box
                sx={{
                    mt:1.5,
                    display: "flex",
                    gap: 4,
                    flexDirection: 'column',

                    "@media screen and (max-width: 47.9375em)": {
                        mt:3,
                    }
                }}
            >
                {!isLoadingCustomers && groupedCustomers.map(({ type, customers: groupCustomers }) => {
                    const typeData = getCustomerTypeData(type, theme);
                    return (
                        <Box key={type}>
                            <Text 
                                bold
                                variant={typeData.labelcolor}
                                sx={{
                                    mb:1,
                                }}
                            >
                                {t(typeData.labeltext)}
                            </Text>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    flexDirection: 'column',
                                }}
                            >
                                {groupCustomers.map((customer) => {
                                    const custTypeData = getCustomerTypeData(customer.db_type ?? 'unknown', theme);
                                    return (
                                        <ButtonBase
                                            key={customer.id}
                                            onClick={() => handleOpen(customer)}
                                            sx={{
                                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                                borderRadius: 3,
                                                p: 1.5,
                                                py:1,
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                width: 1,
                                                transition: "250ms",
                                                "&:hover": {
                                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 1.5,
                                                    alignItems: "center",
                                                }}
                                            >
                                                <ShLabel
                                                    palette="normal"
                                                    size={24}
                                                    fitted
                                                    variant={custTypeData.labelcolor}
                                                    bold
                                                >
                                                    {t(custTypeData.shorttext)}
                                                </ShLabel>
                                                <Text semibold>{customer.name}</Text>
                                            </Box>

                                            <ArrowRightAltIcon sx={{
                                                color: colors.txt["secondary"],
                                            }} />
                                        </ButtonBase>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
                {!isLoadingCustomers && filteredCustomers.length === 0 && (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                        <Text light>{t("no_results")}</Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Customers;
